import React, { useMemo } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import { computePane } from "./paneClip";

// The ants are drawn just OUTSIDE the copied cells (on the grid line), so
// the selection outline that sits just INSIDE the cells stays visible too —
// Excel shows both the copy marker and the selection at the same time.
const ANTS = 2;  // ant line thickness / outward offset, content px
// Above the sticky row/column headers (z 10/20) so the outer ants are not
// hidden at row 1 / column A; the pane clip limits that overlap to ANTS px.
const MARQUEE_Z = { body: 25, col: 26, row: 26, corner: 27 };

/**
 * CopyMarquee — Excel's "marching ants" border around the range the reader
 * just copied (Ctrl+C). Rendered inside VisualOverlay like a chart: one copy
 * per freeze pane, positioned in CONTENT px (the overlay applies the zoom)
 * and re-pinned imperatively on scroll through the data-pane attributes.
 */
function CopyMarquee({
  range, colWidths, rowHeights, hiddenCols, hiddenRows,
  pane = "body", freezeX = HEADER_COL_WIDTH, freezeY = HEADER_ROW_HEIGHT,
}) {
  const box = useMemo(() => {
    if (!range) return null;
    const colHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const rowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));
    const cw = (i) => (colHidden(i) ? 0 : (colWidths[i] || DEFAULT_COL_WIDTH));
    const rh = (i) => (rowHidden(i) ? 0 : (rowHeights[i] || DEFAULT_ROW_HEIGHT));
    let left = HEADER_COL_WIDTH;
    for (let i = 0; i < range.c1; i += 1) left += cw(i);
    let top = HEADER_ROW_HEIGHT;
    for (let i = 0; i < range.r1; i += 1) top += rh(i);
    let width = 0;
    for (let i = range.c1; i <= range.c2; i += 1) width += cw(i);
    let height = 0;
    for (let i = range.r1; i <= range.r2; i += 1) height += rh(i);
    if (width <= 0 || height <= 0) return null;
    return { left: left - ANTS, top: top - ANTS, width: width + 2 * ANTS, height: height + 2 * ANTS };
  }, [range, colWidths, rowHeights, hiddenCols, hiddenRows]);

  if (!box) return null;
  const geom = computePane(pane, box.left, box.top, box.width, box.height, freezeX - ANTS, freezeY - ANTS, 0, 0);
  if (!geom) return null;

  return (
    <div
      className="sev-copy-marquee"
      data-pane={pane}
      data-cl={box.left}
      data-ct={box.top}
      data-cw={box.width}
      data-ch={box.height}
      data-bleed={ANTS}
      aria-hidden="true"
      style={{
        position: "absolute",
        left: box.left + geom.offsetX,
        top: box.top + geom.offsetY,
        width: box.width,
        height: box.height,
        zIndex: MARQUEE_Z[pane] || 25,
        pointerEvents: "none",
        ...(geom.clipPath ? { clipPath: geom.clipPath, WebkitClipPath: geom.clipPath } : null),
      }}
    />
  );
}

export default React.memo(CopyMarquee);
